import { ApiError, apiFetch } from "./client";
import type { DettaglioClienteAgenzia, DettaglioPraticaAgenzia } from "@/types";
import type {
  AgenziaInCarico,
  ClientiReteResponse,
  DashboardReteResponse,
  IncaricoStorico,
  InvitoRete,
  OperatoreAgenzia,
  OperatoreCreato,
  PraticheReteResponse,
  QuotaReteResponse,
  RichiestaRete,
} from "@/types/rete";

/** 403 con `responsabile_disattivato`: il responsabile esiste ma l'admin l'ha spento.
 *  Le pagine /rete/* mostrano «Accesso sospeso» invece del guasto generico. */
export function isAccessoSospeso(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.detail === "responsabile_disattivato";
}

export function getDashboardRete(year?: number, month?: number): Promise<DashboardReteResponse> {
  const qs = new URLSearchParams();
  if (year !== undefined && month !== undefined) {
    qs.set("year", String(year));
    qs.set("month", String(month));
  }
  const q = qs.toString();
  return apiFetch<DashboardReteResponse>(`/rete/dashboard${q ? `?${q}` : ""}`);
}

/** Quota del mese del responsabile che sta guardando. Senza anno/mese il backend
 *  risponde col mese corrente: l'URL della pagina può restare pulito. */
export function getQuotaRete(year?: number, month?: number): Promise<QuotaReteResponse> {
  const qs = new URLSearchParams();
  if (year !== undefined && month !== undefined) {
    qs.set("year", String(year));
    qs.set("month", String(month));
  }
  const q = qs.toString();
  return apiFetch<QuotaReteResponse>(`/rete/quota${q ? `?${q}` : ""}`);
}

export function getAgenzieRete(): Promise<AgenziaInCarico[]> {
  return apiFetch<AgenziaInCarico[]>("/rete/agenzie");
}

export function getStoricoRete(): Promise<IncaricoStorico[]> {
  return apiFetch<IncaricoStorico[]>("/rete/agenzie/storico");
}

export function getPraticheRete(params: {
  limit?: number;
  offset?: number;
  stage?: string | null;
  q?: string;
  agenzia_id?: string | null;
}): Promise<PraticheReteResponse> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  if (params.stage) qs.set("stage", params.stage);
  if (params.q) qs.set("q", params.q);
  if (params.agenzia_id) qs.set("agenzia_id", params.agenzia_id);
  return apiFetch<PraticheReteResponse>(`/rete/pratiche?${qs.toString()}`);
}

export function getPraticaRete(id: string): Promise<DettaglioPraticaAgenzia> {
  return apiFetch<DettaglioPraticaAgenzia>(`/rete/pratiche/${encodeURIComponent(id)}`);
}

export function getClientiRete(params: {
  limit?: number;
  offset?: number;
  q?: string;
  agenzia_id?: string | null;
}): Promise<ClientiReteResponse> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 20));
  qs.set("offset", String(params.offset ?? 0));
  if (params.q) qs.set("q", params.q);
  if (params.agenzia_id) qs.set("agenzia_id", params.agenzia_id);
  return apiFetch<ClientiReteResponse>(`/rete/clienti?${qs.toString()}`);
}

export function getClienteRete(id: string): Promise<DettaglioClienteAgenzia> {
  return apiFetch<DettaglioClienteAgenzia>(`/rete/clienti/${encodeURIComponent(id)}`);
}

export function getInvitiRete(): Promise<InvitoRete[]> {
  return apiFetch<InvitoRete[]>("/rete/inviti");
}

export function creaInvitoRete(nome: string): Promise<InvitoRete> {
  return apiFetch<InvitoRete>("/rete/inviti", {
    method: "POST",
    body: JSON.stringify({ nome }),
  });
}

export function toggleInvitoRete(id: number): Promise<InvitoRete> {
  return apiFetch<InvitoRete>(`/rete/inviti/${id}/toggle`, { method: "POST" });
}

export function getRichiesteRete(): Promise<RichiestaRete[]> {
  return apiFetch<RichiestaRete[]>("/rete/richieste");
}

export function getOperatoriAgenzia(accountId: string): Promise<OperatoreAgenzia[]> {
  return apiFetch<OperatoreAgenzia[]>(`/rete/agenzie/${encodeURIComponent(accountId)}/operatori`);
}

export function creaOperatoreAgenzia(
  accountId: string,
  body: { nome: string; cognome: string; email: string; cellulare: string },
): Promise<OperatoreCreato> {
  return apiFetch<OperatoreCreato>(`/rete/agenzie/${encodeURIComponent(accountId)}/operatori`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function statoOperatoreAgenzia(accountId: string, userId: string, attivo: boolean)
  : Promise<{ user_id: string; attivo: boolean }> {
  return apiFetch(`/rete/agenzie/${encodeURIComponent(accountId)}/operatori/${encodeURIComponent(userId)}/stato`, {
    method: "POST",
    body: JSON.stringify({ attivo }),
  });
}

export function benvenutoOperatoreAgenzia(accountId: string, userId: string): Promise<{ welcome_inviata: boolean }> {
  return apiFetch(`/rete/agenzie/${encodeURIComponent(accountId)}/operatori/${encodeURIComponent(userId)}/benvenuto`, {
    method: "POST",
  });
}
